import { useState } from "react";
import {
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Card,
  Stack,
  Tooltip,
  Divider,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

import {
  Delete as DeleteIcon,
  Groups as GroupsIcon,
  GroupAdd as GroupAddIcon,
  Security as SecurityIcon,
} from "@mui/icons-material";
import useDeleteData from "@/hooks/deleteHooks/useDeleteData";
import usePostJsonData from "@/hooks/postHooks/usePostJsonData";
import NewDeleteModal from "@components/newDeleteModal";
import Toast from "@/components/toast";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";
import { useQueryClient } from "@tanstack/react-query";
import useAuthenticatedQuery from "@/hooks/useAuthenticatedQuery";
import { useTranslation } from "react-i18next";
import OrganizationHeader from "./components/OrganizationHeader";

function UserGroups() {
  const { t } = useTranslation();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { handleApiError, toast, setToast, handleToastClose } =
    useApiErrorHandler();

  const [itemToDelete, setItemToDelete] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [newUserGroup, setNewUserGroup] = useState("");

  const { mutate: postUserGroup, isLoading: postIsLoading } = usePostJsonData();
  const { mutate: deleteUserGroup } = useDeleteData(
    "/api/v1/org-admin/user-groups",
    ["usergroups"]
  );

  const {
    data: userGroups,
    isLoading: userGroupsLoading,
    error: errorUserGroups,
  } = useAuthenticatedQuery(["usergroups"], "/api/v1/metadata/user-groups");

  if (userGroupsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress
            size={48}
            aria-label={t("userGroups.loadingAria")}
          />
          <Typography variant="body1" color="text.secondary">
            {t("userGroups.loadingMessage")}
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (errorUserGroups) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="error" gutterBottom>
          {t("userGroups.errorLoading")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("userGroups.errorMessage")}
        </Typography>
      </Box>
    );
  }

  const addItem = (newItem, endpoint) => {
    if (newItem.trim() === "") return;

    const newObject = { name: newItem };

    postUserGroup(
      { url: endpoint, data: newObject },
      {
        onSuccess: () => {
          setNewUserGroup("");
          setToast({
            open: true,
            message: t("userGroups.successAddToast"),
            severity: "success",
          });
          queryClient.invalidateQueries(["user-groups"]);
        },
        onError: (err) => {
          handleApiError(err);
        },
      }
    );
  };

  const confirmDelete = (id) => {
    setItemToDelete(id);
    setConfirmOpen(true);
  };

  const handleDelete = async () => {
    try {
      await deleteUserGroup(itemToDelete);
      setToast({
        open: true,
        message: t("userGroups.successDeleteToast"),
        severity: "success",
      });
    } catch (err) {
      handleApiError(err);
    } finally {
      setItemToDelete(null);
      setConfirmOpen(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && newUserGroup.trim() !== "") {
      addItem(newUserGroup, "/api/v1/org-admin/user-groups");
    }
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      {/* Compact Enterprise Header */}
      <OrganizationHeader
        icon={SecurityIcon}
        title={t("userGroups.headerTitle")}
        subtitle={t("userGroups.headerSubtitle")}
        count={userGroups?.length || 0}
      />

      <Box sx={{ px: 4, pb: 4 }}>
        {/* Add User Group Section */}
        <Card
          elevation={3}
          sx={{
            mb: 4,
            overflow: "visible",
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
          }}
        >
          <Box sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{ mb: 3, color: "text.primary", fontWeight: 600 }}
            >
              {t("userGroups.addSectionTitle")}
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                label={t("userGroups.newUserGroupLabel")}
                size="small"
                variant="outlined"
                value={newUserGroup}
                onChange={(e) => setNewUserGroup(e.target.value)}
                onKeyPress={handleKeyPress}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: theme.shadows[2],
                    },
                    "&.Mui-focused": {
                      boxShadow: theme.shadows[4],
                    },
                  },
                }}
                aria-label={t("userGroups.newUserGroupAria")}
              />
              <Button
                variant="contained"
                size="large"
                color="primary"
                onClick={() =>
                  addItem(newUserGroup, "/api/v1/org-admin/user-groups")
                }
                disabled={postIsLoading || newUserGroup.trim() === ""}
                startIcon={
                  postIsLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <GroupAddIcon />
                  )
                }
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: 140,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.8)} 100%)`,
                  "&:hover": {
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)} 0%, ${alpha(theme.palette.primary.main, 0.7)} 100%)`,
                    boxShadow: theme.shadows[8],
                    transform: "translateY(-2px)",
                  },
                  "&.Mui-disabled": {
                    background: `linear-gradient(135deg, ${alpha(theme.palette.grey[400], 0.6)} 0%, ${alpha(theme.palette.grey[300], 0.4)} 100%)`,
                    color: alpha(theme.palette.text.disabled, 0.7),
                    boxShadow: "none",
                    transform: "none",
                  },
                  transition: "all 0.3s ease",
                }}
                aria-label={t("userGroups.addButtonAria")}
              >
                {t("userGroups.addButton")}
              </Button>
            </Stack>
          </Box>
        </Card>

        {/* User Groups List */}
        <Card
          elevation={3}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              p: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <GroupsIcon sx={{ color: "secondary.main", fontSize: 28 }} />
              <Typography variant="h6" fontWeight="bold" color="text.primary">
                {t("userGroups.listTitle")}
              </Typography>
            </Stack>
          </Box>

          <Box sx={{ p: 3 }}>
            {userGroups?.length > 0 ? (
              <List sx={{ p: 0 }}>
                {userGroups.map((group, index) => (
                  <Box key={group.id}>
                    <Card
                      elevation={1}
                      sx={{
                        mb: 2,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: theme.shadows[6],
                          borderColor: "secondary.main",
                          "& .group-actions": {
                            opacity: 1,
                          },
                        },
                      }}
                    >
                      <ListItem
                        sx={{
                          p: 2,
                          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor: alpha(theme.palette.secondary.main, 0.1),
                              border: "2px solid",
                              borderColor: alpha(
                                theme.palette.secondary.main,
                                0.2
                              ),
                              width: 56,
                              height: 56,
                              mr: 2,
                            }}
                          >
                            <GroupsIcon
                              sx={{
                                color: "secondary.main",
                                fontSize: 28,
                              }}
                            />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography
                              variant="h6"
                              fontWeight="600"
                              color="text.primary"
                              sx={{ mb: 0.5 }}
                            >
                              {group.name}
                            </Typography>
                          }
                          /*  secondary={
                            <Typography variant="body2" color="text.secondary">
                              {t("userGroups.groupId", { id: group.id })}
                            </Typography>
                          } */
                        />
                        <Box
                          className="group-actions"
                          sx={{
                            opacity: 0.7,
                            transition: "opacity 0.3s ease",
                          }}
                        >
                          <Tooltip title={t("userGroups.deleteTooltip")}>
                            <IconButton
                              color="error"
                              onClick={() => confirmDelete(group.id)}
                              sx={{
                                borderRadius: 2,
                                "&:hover": {
                                  bgcolor: alpha(theme.palette.error.main, 0.1),
                                  transform: "scale(1.1)",
                                },
                                transition: "all 0.3s ease",
                              }}
                              aria-label={t("userGroups.deleteButtonAria", {
                                name: group.name,
                              })}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ListItem>
                    </Card>
                    {index < userGroups.length - 1 && (
                      <Divider sx={{ my: 1, opacity: 0.3 }} />
                    )}
                  </Box>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Box
                  sx={{
                    display: "inline-flex",
                    p: 3,
                    borderRadius: "50%",
                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                    mb: 3,
                  }}
                >
                  <GroupsIcon
                    sx={{
                      fontSize: 48,
                      color: "secondary.main",
                      opacity: 0.7,
                    }}
                  />
                </Box>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  gutterBottom
                  fontWeight="500"
                >
                  {t("userGroups.noUserGroups")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("userGroups.noUserGroupsDesc")}
                </Typography>
              </Box>
            )}
          </Box>
        </Card>
      </Box>

      {/* Delete Confirmation Modal */}
      <NewDeleteModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        itemType={t("userGroups.itemType")}
      />

      {/* Toast Notification */}
      <Toast
        open={toast.open}
        onClose={handleToastClose}
        severity={toast.severity}
        message={toast.message}
      />
    </Box>
  );
}

export default UserGroups;
